import type { MoneyDto, SaleItem } from '../api/admin.api'
import { adminChargeLabel, adminJourneyId } from './admin-sale-charge'
import { saleFinancialImpact } from './admin-sale-events'

export type AdminSaleRefundTarget = {
  sale: SaleItem
  saleId: string
  label: string
  productName: string
  refundAmount: MoneyDto
  settlementImpact: MoneyDto
  canRequestRefund: boolean
  unavailableReason?: string
  hasPartialRefund: boolean
  sharedLineCount: number
}

export { adminJourneyId as adminSaleJourneyId } from './admin-sale-charge'

export function buildAdminSaleRefundTargets(
  currentSale: SaleItem,
  journeySales: readonly SaleItem[],
): AdminSaleRefundTarget[] {
  const journeyId = adminJourneyId(currentSale)
  const uniqueSales = new Map<string, SaleItem>()
  for (const sale of [...journeySales, currentSale]) {
    if (adminJourneyId(sale) === journeyId) {
      uniqueSales.set(sale.id, sale)
    }
  }

  return [...uniqueSales.values()]
    .sort((left, right) => {
      const leftOrder = chargeOrder(left, journeyId)
      const rightOrder = chargeOrder(right, journeyId)
      return leftOrder - rightOrder || left.createdAt.localeCompare(right.createdAt)
    })
    .map(toRefundTarget)
}

function toRefundTarget(sale: SaleItem): AdminSaleRefundTarget {
  const unavailableReason = refundUnavailableReason(sale)
  const lineCount = sale.lines?.length ?? 0
  const additionalItems = Math.max(0, lineCount - 1)
  const baseProductName = sale.productName ?? `Produto ${sale.productId}`
  return {
    sale,
    saleId: sale.id,
    label: adminChargeLabel(sale),
    productName:
      additionalItems > 0
        ? `${baseProductName} + ${additionalItems} ${additionalItems === 1 ? 'item adicional' : 'itens adicionais'}`
        : baseProductName,
    refundAmount:
      sale.refundableAmount ??
      (sale.amountPaid.currency === sale.grossAmount.currency
        ? saleFinancialImpact(sale).active
        : sale.amountPaid),
    settlementImpact: saleFinancialImpact(sale).active,
    canRequestRefund: unavailableReason === undefined,
    ...(unavailableReason !== undefined ? { unavailableReason } : {}),
    hasPartialRefund:
      sale.financialEvents?.some((event) => event.kind === 'PARTIAL_REFUND') === true,
    sharedLineCount: lineCount,
  }
}

function refundUnavailableReason(sale: SaleItem): string | undefined {
  if (
    !sale.refundableAmount &&
    sale.amountPaid.currency !== sale.grossAmount.currency &&
    sale.financialEvents?.some((event) => event.kind === 'PARTIAL_REFUND')
  )
    return 'Valor restante na moeda do comprador ainda indisponível.'
  if (sale.status !== 'PAID') return 'Cobrança indisponível neste status.'
  if (sale.gateway !== 'STRIPE') return 'Gateway sem reembolso manual.'
  if (!sale.gatewayPaymentId) return 'Referência do gateway indisponível.'
  return undefined
}

function chargeOrder(sale: SaleItem, journeyId: string): number {
  if (sale.id === journeyId || sale.chargeType === 'CHECKOUT') return 0
  if (sale.chargeType === 'UPSELL') return 1
  if (sale.chargeType === 'DOWNSELL') return 2
  if (sale.chargeType === 'RENEWAL' || sale.isRenewal) return 3
  return 4
}
